import React, {useEffect, useState} from 'react';
import AnalyticsUI from '../view/AnalyticsUI';
import {inject, observer} from 'mobx-react';
import AnalyticsStore from '../store/AnalyticsStore';
import {useNavigate, useParams} from 'react-router-dom';
import {downloadCSV} from '../../../../utils/downloadCsv';
import {analyticsText} from '../../analyticsText';
import {hasValue} from '../../../../utils/typecheck';

interface Props {
  analyticsStore?: AnalyticsStore;
}

const AnalyticsContainer = inject('analyticsStore')(
  observer(({analyticsStore}: Props) => {
    const [search, setSearch] = useState('');
    const [selectedCourse, setSelectedCourse] = useState('');
    const [orderAsc, setOrderAsc] = useState(true);
    const [page, setPage] = useState(1);
    const [exporting, setExporting] = useState(false);
    const {organizationId} = useParams();
    const navigate = useNavigate();

    useEffect(() => {
      if (organizationId) {
        analyticsStore!.getCoursesList(organizationId);
        analyticsStore!.getLearnerStatistics(organizationId);
      }
    }, []);

    useEffect(() => {
      if (selectedCourse) {
        navigate(`course/${selectedCourse}/learners`);
        setSelectedCourse('');
      }
    }, [selectedCourse]);

    useEffect(() => {
      if (!organizationId) return;
      let active = true;
      analyticsStore!
        .getLearnersPage(organizationId, page - 1, search, orderAsc)
        .then(data => {
          if (active) analyticsStore!.setLearnersPage(data);
        });
      return () => {
        active = false;
      };
    }, [page, search, orderAsc]);

    const onSearch = (value: string) => {
      setSearch(value);
      setPage(1);
    };

    const onChangeOrder = (value: boolean) => {
      setOrderAsc(value);
      setPage(1);
    };

    const onExport = async () => {
      if (!organizationId || exporting) return;
      setExporting(true);
      const all = await analyticsStore!.getAllLearners(
        organizationId,
        search,
        orderAsc,
      );
      setExporting(false);
      if (!all) return;
      downloadCSV(
        [
          [
            analyticsText.learner,
            analyticsText.avgScore,
            ...analyticsStore!.coursesList.map(c => c.name),
          ],
          [
            analyticsText.overallScore,
            hasValue(analyticsStore!.coursesAnalytics!.globalAvgScore)
              ? Math.round(analyticsStore!.coursesAnalytics!.globalAvgScore) +
                '%'
              : '-',
            ...analyticsStore!.coursesList.map(c =>
              hasValue(c.avgScore) ? Math.round(c.avgScore || 0) + '%' : '-',
            ),
          ],
          ...all.learners.map(l => [
            l.name,
            hasValue(l.avgScore) ? Math.round(l.avgScore || 0) + '%' : '-',
            ...analyticsStore!.coursesList.map(c => {
              const record = all.grades[l.learnerId]?.find(
                g => g.courseId === c.id,
              );
              if (!record) {
                return '-';
              }
              if (c.isGradable && hasValue(record.grade)) {
                return Math.round(record.grade || 0) + '%';
              }
              if (!record.isStarted) {
                return analyticsText.notStarted;
              }
              return c.isGradable
                ? analyticsText.inProgress
                : analyticsText.completed;
            }),
          ]),
        ],
        'courses_overview.csv',
      );
    };

    return (
      <AnalyticsUI
        search={search}
        setSearch={onSearch}
        learners={analyticsStore!.learnersList}
        courses={analyticsStore!.coursesList}
        grades={analyticsStore!.gradesMap}
        page={page}
        pagesCount={analyticsStore!.learnersPagesCount}
        onChangePage={setPage}
        coursesAnalytics={analyticsStore!.coursesAnalytics}
        learnerStatistics={analyticsStore!.learnerStatistics}
        isLoading={analyticsStore!.isLoading}
        coursesLoading={!analyticsStore!.hasLoadedCourses}
        statisticsLoading={!analyticsStore!.hasLoadedStatistics}
        learnersLoading={!analyticsStore!.hasLoadedLearners}
        orderAsc={orderAsc}
        setOrderAsc={onChangeOrder}
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        onExport={onExport}
        exporting={exporting}
      />
    );
  }),
);

export default AnalyticsContainer;
